import { DatePicker, ErrorMessage } from '@/components';
import type { DatePickerProps } from 'antd';
import enUS from 'antd/es/date-picker/locale/en_US';
import jaJP from 'antd/es/date-picker/locale/ja_JP';
import type { Dayjs } from 'dayjs';
import type { CSSProperties } from 'react';
import {
  Controller,
  type FieldErrors,
  type FieldValues,
  type Path,
} from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Form } from '.';
import { FieldLabel, type FieldLabelSize } from './FieldLabel';
import type { FormControlProp } from './types';
import 'dayjs/locale/ja';

type ExtractDatePickerProps<T> = T extends infer R extends { picker?: 'date' }
  ? R
  : never;
type DisabledTime = ExtractDatePickerProps<DatePickerProps>['disabledTime'];

type Props<T extends FieldValues, U extends boolean> = {
  name: Path<T>;
  label?: string;
  control: FormControlProp<T>;
  errors: FieldErrors;
  required?: boolean;
  picker?: 'date' | 'week' | 'month' | 'quarter' | 'year';
  hint?: string;
  placeholder?: string;
  showTime?: boolean;
  showNow?: boolean;
  hiddenErrorMessage?: boolean;
  disabledTime?: DisabledTime;
  wrapperStyle?: CSSProperties;
  fieldStyle?: CSSProperties;
  shouldCoexistOnChange?: boolean;
  labelSize?: FieldLabelSize;
  allowClear?: U;
  onChange?: (
    date: U extends false ? Dayjs : Dayjs | null,
    dateString: string | string[],
  ) => void;
} & Pick<DatePickerProps, 'picker' | 'disabledDate' | 'disabled' | 'value'>;

export const DatePickerField = <T extends FieldValues, U extends boolean>({
  name,
  label,
  control,
  errors,
  required,
  picker,
  hint,
  placeholder = '',
  disabled,
  showTime,
  showNow,
  hiddenErrorMessage = false,
  disabledDate,
  disabledTime,
  wrapperStyle,
  fieldStyle,
  onChange,
  shouldCoexistOnChange = false,
  allowClear,
  labelSize = 'medium',
  value,
}: Props<T, U>) => {
  const { i18n } = useTranslation();
  const locale = i18n.language === 'ja' ? jaJP : enUS;
  return (
    <Form.Item
      style={wrapperStyle}
      label={
        label && (
          <FieldLabel
            text={label}
            required={required}
            hint={hint}
            size={labelSize}
          />
        )
      }
    >
      <div>
        <Controller
          control={control}
          name={name}
          render={({ field }) => {
            const { onChange: fieldOnChange, ...restField } = field;
            return (
              <DatePicker
                {...restField}
                picker={picker}
                value={value ?? field.value}
                placeholder={placeholder}
                disabled={disabled}
                disabledDate={disabledDate}
                disabledTime={disabledTime}
                showTime={showTime}
                showNow={showNow}
                locale={locale}
                style={fieldStyle}
                onChange={(value, dateString) => {
                  if (shouldCoexistOnChange || !onChange) {
                    fieldOnChange(value, dateString);
                  }
                  onChange?.(value, dateString);
                }}
                allowClear={allowClear}
              />
            );
          }}
        />
      </div>
      {!hiddenErrorMessage && <ErrorMessage errors={errors} name={name} />}
    </Form.Item>
  );
};
